import {
   Component,
   AfterViewInit,
   ViewChild,
   ElementRef,
   Input,
   OnInit,
} from '@angular/core';
import { Chart } from 'chart.js';
import { ChangeDetectorRef } from '@angular/core';
import { StockFilterService } from 'src/app/features/stock-screener/services/stock-filter.service';
// import ChartDataLabels from 'chartjs-plugin-datalabels';
// Chart.plugins.register(ChartDataLabels);

interface DocumentType {
   label: string;
   year: number;
   quarter: number; // Quarter of the record (1, 2, 3, or 4)
   value: number;
}

@Component({
    selector: 'app-balancesheet-chart',
    templateUrl: './balancesheet-chart.component.html',
    styleUrls: ['./balancesheet-chart.component.scss'],
    standalone: false
})
export class BalancesheetChartComponent implements OnInit, AfterViewInit {
   chart!: Chart;
   @Input() chartKey: string = '';
   @Input() specialArray: any[] = [];
   quarterlyData: any[] = [];
   quarters: string[] = [];

   setLoadingState: boolean = true;
   period: number[] = [2023, 2024];
   page_num: number = 1;

   // Define the assets and liabilities properties
   assets: (number | null)[] = [];
   liabilities: (number | null)[] = [];

   labels: string[] = ['totalAssets', 'totalLiabilities'];

   loading: boolean = true;
   hasData: boolean = false;

   noData: boolean = false;

   constructor(
      private financialService: StockFilterService,
      private cd: ChangeDetectorRef
   ) { }

   ngOnInit() {
      this.getQuarterlyData().then(() => {
         this.processData();
         this.initializeChart();
      });
   }

   async getQuarterlyData() {
      await this.getNewActualQuarterlyData(
         this.chartKey,
         'quarterly',
         this.page_num,
         this.quarterlyData
      );
   }

   async getNewActualQuarterlyData(
      ticker: string,
      freq: string,
      page_num: number,
      targetArray: any[]
   ) {
      try {
         let allData: any[] = [];
         let hasMoreData = true;
         const topLabels = [
            // Assets
            'totalAssets', // Total Assets
            'totalLiabilities', // Total Liabilities
         ];

         // Remove hardcoded quarters - will extract dynamically

         while (hasMoreData) {
            const response = await this.financialService.getNewIncomeData(
               ticker,
               freq,
               page_num
            );

            if (response?.hits?.length > 0) {
               allData.push(...response.hits);
               hasMoreData = response.hits.length >= 100;
               if (hasMoreData) page_num++;
            } else {
               hasMoreData = false;
            }
         }

         const labelMap = new Map<string, Record<string, number>>();

         allData.forEach((hit: any) => {
            const doc = hit.document;
            if (!doc?.period) {
               console.warn('Skipping entry with missing period:', doc);
               return;
            }

            const quarterLabel = this.formatQuarterLabel(doc.period);
            if (!quarterLabel) return;

            topLabels.forEach((label) => {
               const value = parseFloat(doc[label]);
               if (!isNaN(value)) {
                  if (!labelMap.has(label)) {
                     labelMap.set(label, { [quarterLabel]: value });
                  } else {
                     labelMap.get(label)![quarterLabel] = value;
                  }
               } else {
                  console.warn(`Invalid value for ${label} in quarter ${quarterLabel}:`, doc[label]);
               }
            });
         });

         const result = Array.from(labelMap.entries()).map(([label, data]) => ({
            label,
            data,
         }));


         this.setLoadingState = false;
         this.quarterlyData = [...result]; // Update the target array

         // Extract latest 4 quarters from the data
         this.quarters = this.extractLatestQuarters(this.quarterlyData);

         this.cd.detectChanges(); // Trigger change detection
      } catch (error) {
         this.setLoadingState = false;
         console.error('Error fetching quarterly data:', error);
      }
   }

   processData() {
      if (this.quarters.length === 0) {
         return;
      }

      const requiredQuarters = this.quarters;
      const quarterPositions: Record<string, number> = {};
      this.quarters.forEach((quarter, index) => {
         quarterPositions[quarter] = index;
      });

      this.specialArray.forEach((item) => {
         if (this.labels.includes(item.label)) {
            let tempArray: (number | null)[] = new Array(this.quarters.length).fill(null);

            Object.entries(item.data).forEach(([quarter, value]) => {
               if (
                  requiredQuarters.includes(quarter) &&
                  quarterPositions.hasOwnProperty(quarter)
               ) {
                  const position = quarterPositions[quarter];
                  if (position !== undefined) {
                     tempArray[position] = value as number;
                  }
               }
            });

            const filteredTempArray = tempArray.filter(
               (_, index) => index === 0 || index === tempArray.length - 1
            );
            const finalArray = tempArray.every((v) => v == null)
               ? []
               : filteredTempArray.length === 2
                  ? filteredTempArray
                  : tempArray;

            switch (item.label) {
               case 'Net sales':
               case 'Cost of sales':
                  this.assets = finalArray;
                  break;
               case 'Gross margin':
               case 'Provision for income taxes':
               case 'Net income':
                  this.liabilities = finalArray;
                  break;
            }
         }
      });
   }

   formatQuarterLabel(dateString: string): string | null {
      if (!dateString) return null;

      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
         console.error('Invalid date:', dateString);
         return null;
      }

      const month = date.getMonth() + 1;
      const year = date.getFullYear().toString().slice(-2);
      const quarter = Math.ceil(month / 3);

      return `Q${quarter}'${year}`;
   }

   private extractLatestQuarters(quarterlyData: any[]): string[] {
      if (!quarterlyData || quarterlyData.length === 0) {
         return [];
      }

      // Collect all unique quarters from all data items
      const allQuarters = new Set<string>();
      quarterlyData.forEach(item => {
         if (item.data) {
            Object.keys(item.data).forEach(quarter => {
               allQuarters.add(quarter);
            });
         }
      });

      // Convert to array and sort quarters chronologically
      const sortedQuarters = Array.from(allQuarters).sort((a, b) => {
         // Parse quarter format like "Q1'25" to sort chronologically
         const parseQuarter = (q: string) => {
            const [quarter, year] = q.split("'");
            const quarterNum = parseInt(quarter.substring(1));
            const fullYear = parseInt(year) + 2000; // Convert '25 to 2025
            return fullYear * 4 + quarterNum; // Create sortable number
         };

         return parseQuarter(a) - parseQuarter(b); // Sort ascending (oldest first)
      });

      // Return the latest 4 quarters (last 4 from the sorted array)
      return sortedQuarters.slice(-4);
   }

   ngAfterViewInit() {
      // If data is already loaded, initialize the chart
      if (this.quarterlyData.length) {
         this.initializeChart();
      }
   }

   @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;

   private sortQuarterLabels(labels: string[]): string[] {
      // Use the dynamic quarters array for sorting
      return labels.sort((a, b) => {
         const indexA = this.quarters.indexOf(a);
         const indexB = this.quarters.indexOf(b);
         return indexA - indexB;
      });
   }

   private initializeChart() {
      if (!this.chartCanvas || !this.chartCanvas.nativeElement) {
         console.error('Chart canvas not found');
         return;
      }

      const ctx = this.chartCanvas.nativeElement.getContext('2d');
      if (!ctx) {
         console.error('Could not get 2d context from canvas');
         return;
      }

      if (!this.quarterlyData.length) {
         console.error('No quarterly data available for chart');
         return;
      }


      const labels = this.quarters;

      // Ensure the canvas has width and height
      this.chartCanvas.nativeElement.width =
         this.chartCanvas.nativeElement.offsetWidth;
      this.chartCanvas.nativeElement.height = 400; // Set an appropriate height

      const datasets = this.quarterlyData.map((item) => {
         return {
            label: this.formatLabel(item.label),
            data: labels.map((quarter) => {
               const value = item.data[quarter];
               return (value || 0) * 1_000_000; // Multiply by 10,00,000
            }),
            backgroundColor: this.getBackgroundColor(item.label),
            borderRadius: 10,
            // Remove these fixed size properties
            // barThickness: 30,
            // maxBarThickness: 50,
            // Add these instead for controlling spacing
            categoryPercentage: 0.7,  // Spacing between quarters
            barPercentage: 0.8        // Spacing between bars within a quarter
         };
      });

      if (this.chart) {
         this.chart.destroy();
      }

      // For Chart.js v2 syntax
      this.chart = new Chart(ctx, {
         type: 'bar',
         data: {
            labels,
            datasets,
         },
         options: {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
               padding: { top: 30 },
            },
            plugins: {
               legend: {
                  display: true,
                  position: 'bottom',
               },
               // @ts-ignore
               datalabels: {
                  display: false,
               },
               tooltip: {
                  callbacks: {
                     label: (tooltipItem: any) => {
                        const value = tooltipItem.raw; // Get the raw value
                        return this.formatValue(value); // Format the value
                     },
                  },
               },
            },
            scales: {
               x:
               {
                  display: true,
                  grid: {
                     display: false,
                     color: '#000000'
                  },
               },
               y:
               {
                  display: true,
                  ticks: {
                     callback: (value: any) => this.formatValue(value as number),
                  },
                  grid: {
                     display: true,
                     color: '#FFFFFF',
                     lineWidth: 3,
                     drawBorder: false
                  },
               },
            },
         },
      });

   }

   private formatLabel(label: string): string {
      // Convert camelCase to Title Case with spaces
      switch (label) {
         case 'totalAssets':
            return 'Total assets';
         case 'totalLiabilities':
            return 'Total liabilities';
         default:
            return label
               .replace(/([A-Z])/g, ' $1')
               .replace(/^./, (str) => str.toUpperCase());
      }
   }
   formatValue = (value: number | undefined): string => {
      if (value === undefined || value === null) {
         return '-'; // Return dash if no value
      }

      const absValue = Math.abs(value); // Work with absolute value for formatting

      if (absValue >= 1_000_000_000) {
         return (value / 1_000_000_000).toFixed(1) + 'B'; // Convert to billions
      }

      if (absValue >= 1_000_000) {
         return (value / 1_000_000).toFixed(1) + 'M'; // Convert to millions
      }

      return value.toString(); // Return the number as a string if it is less than a million
   };

   private getBackgroundColor(label: string): string {
      switch (label) {
         case 'totalAssets':
            return 'rgba(255, 99, 132, 1)';

         case 'totalLiabilities':
            return 'rgba(75, 192, 192, 1)'; // Teal
         default:
            return 'rgba(0, 0, 0, 1)';
      }
   }
}
